import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Goal, GoalResponse } from "../types";
import { getGoals, getResponsesForRange } from "../repository/daysRepository";

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const CalendarView: React.FC = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [goals, setGoals] = useState<Goal[]>([]);
  const [responses, setResponses] = useState<GoalResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = formatLocalDate(firstDay);
      const endDate = formatLocalDate(lastDay);

      const [goalsData, responsesData] = await Promise.all([
        getGoals(),
        getResponsesForRange(startDate, endDate),
      ]);

      setGoals(goalsData.filter((g) => g.status === "active"));
      setResponses(responsesData);
    } catch (err) {
      console.error("Failed to load calendar data:", err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const prevMonth = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); }
    else setMonth(month - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); }
    else setMonth(month + 1);
  };

  // Build calendar grid
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startDow = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  // Build response map: date -> set of goal_uuids
  const responsesByDate = new Map<string, Set<string>>();
  responses.forEach((r) => {
    const existing = responsesByDate.get(r.date) || new Set();
    existing.add(r.goal_uuid);
    responsesByDate.set(r.date, existing);
  });

  const activeGoalCount = goals.length;

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) currentWeek.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  const monthName = firstDayOfMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayStr = formatLocalDate(new Date());

  return (
    <div className="calendar-page">
      <div className="calendar-nav">
        <button className="btn-icon" onClick={prevMonth}>&larr;</button>
        <h2>{monthName}</h2>
        <button className="btn-icon" onClick={nextMonth}>&rarr;</button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="calendar-grid">
          <div className="calendar-header-row">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="calendar-header-cell">{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="calendar-week">
              {week.map((day, di) => {
                if (day === null) {
                  return <div key={di} className="calendar-cell empty" />;
                }

                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const responded = responsesByDate.get(dateStr);
                const respondedCount = responded ? responded.size : 0;
                const isToday = dateStr === todayStr;

                let statusClass = "";
                if (activeGoalCount > 0 && respondedCount > 0) {
                  statusClass = respondedCount >= activeGoalCount ? "complete" : "partial";
                }

                return (
                  <div
                    key={di}
                    className={`calendar-cell ${statusClass} ${isToday ? "today" : ""}`}
                    onClick={() => navigate(`/?date=${dateStr}`)}
                  >
                    <span className="calendar-day">{day}</span>
                    {statusClass && <span className={`calendar-dot ${statusClass}`} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div className="calendar-legend">
        <span><span className="legend-dot complete" /> All goals answered</span>
        <span><span className="legend-dot partial" /> Some goals answered</span>
      </div>
    </div>
  );
};

export default CalendarView;
